import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { useToast } from '@/components/ui/Toast';
import { 
  EmploymentType,
  EmploymentStatus,
  PayType,
  PayFrequency,
  TaxScale,
  EMPLOYMENT_TYPES,
  EMPLOYMENT_STATUSES,
  PAY_TYPES,
  PAY_FREQUENCIES,
  TAX_SCALES,
} from '@/types';
import { useEmployee, useCreateEmployee, useUpdateEmployee } from '@/hooks/useEmployees';
import { useSuperFunds } from '@/hooks/useSuperannuation';
import { formatDate } from '@/lib/utils';

export function EmployeeForm() {
  const { id } = useParams<{ id: string }>();

  const navigate = useNavigate();
  const { toast } = useToast();
  const isEditing = !!id;


  const { data: existingEmployee, isLoading: isLoadingEmployee } = useEmployee(id!);
  const { data: superFundsData } = useSuperFunds();
  const createEmployee = useCreateEmployee();
  const updateEmployee = useUpdateEmployee();

  const superFunds = superFundsData?.data || [];

  const [formData, setFormData] = useState({
    // Personal details
    code: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    mobile: '',
    dateOfBirth: '',
    gender: 'prefer_not_to_say' as 'male' | 'female' | 'other' | 'prefer_not_to_say',
    
    // Address
    streetAddress1: '',
    streetAddress2: '',
    suburb: '',
    state: '',
    postcode: '',
    country: 'Australia',
    
    // Employment details
    employmentType: 'full_time' as EmploymentType,
    employmentStatus: 'active' as EmploymentStatus,
    startDate: formatDate(new Date()),
    endDate: '',
    jobTitle: '',
    department: '',
    
    // Pay details
    payType: 'hourly' as PayType,
    payRate: 0,
    payFrequency: 'fortnightly' as PayFrequency,
    
    // Tax declaration
    tfn: '',
    taxScale: 'regular' as TaxScale,
    claimTaxFreeThreshold: true,
    hasHelpDebt: false,
    hasFinancialSupplementDebt: false,
    
    // Superannuation
    superFundId: '',
    superMemberNumber: '',
    superContributionRate: 11.5, // Current SG rate
    
    // Bank account
    bankBsb: '',
    bankAccountNumber: '',
    bankAccountName: '',
  });

  useEffect(() => {
    if (existingEmployee) {
      setFormData({
        code: existingEmployee.code || '',
        firstName: existingEmployee.firstName || '',
        lastName: existingEmployee.lastName || '',
        email: existingEmployee.email || '',
        phone: existingEmployee.phone || '',
        mobile: existingEmployee.mobile || '',
        dateOfBirth: existingEmployee.dateOfBirth || '',
        gender: existingEmployee.gender || 'prefer_not_to_say',
        streetAddress1: existingEmployee.address?.streetAddress1 || '',
        streetAddress2: existingEmployee.address?.streetAddress2 || '',
        suburb: existingEmployee.address?.suburb || '',
        state: existingEmployee.address?.state || '',
        postcode: existingEmployee.address?.postcode || '',
        country: existingEmployee.address?.country || 'Australia',
        employmentType: existingEmployee.employmentType || 'full_time',
        employmentStatus: existingEmployee.employmentStatus || 'active',
        startDate: existingEmployee.startDate || formatDate(new Date()),
        endDate: existingEmployee.endDate || '',
        jobTitle: existingEmployee.jobTitle || '',
        department: existingEmployee.department || '',
        payType: existingEmployee.payType || 'hourly',
        payRate: existingEmployee.payRate || 0,
        payFrequency: existingEmployee.payFrequency || 'fortnightly',
        tfn: existingEmployee.tfn || '',
        taxScale: existingEmployee.taxScale || 'regular',
        claimTaxFreeThreshold: existingEmployee.claimTaxFreeThreshold ?? true,
        hasHelpDebt: existingEmployee.hasHelpDebt || false,
        hasFinancialSupplementDebt: existingEmployee.hasFinancialSupplementDebt || false,
        superFundId: existingEmployee.superFundId || '',
        superMemberNumber: existingEmployee.superMemberNumber || '',
        superContributionRate: existingEmployee.superContributionRate || 11.5,
        bankBsb: existingEmployee.bankBsb || '',
        bankAccountNumber: existingEmployee.bankAccountNumber || '',
        bankAccountName: existingEmployee.bankAccountName || '',
      });
    }
  }, [existingEmployee]);

  const handleChange = (field: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload = {
      ...formData,
      address: {
        streetAddress1: formData.streetAddress1,
        streetAddress2: formData.streetAddress2 || undefined,
        suburb: formData.suburb,
        state: formData.state,
        postcode: formData.postcode,
        country: formData.country,
      },
    };

    try {
      if (isEditing) {
        await updateEmployee.mutateAsync({ id, data: payload });
        toast({
          title: 'Success',
          description: 'Employee updated successfully',
          variant: 'success',
        });
        navigate(`/payroll/employees/${id}`);
      } else {
        const newEmployee = await createEmployee.mutateAsync(payload);
        toast({
          title: 'Success',
          description: 'Employee created successfully',
          variant: 'success',
        });
        navigate(`/payroll/employees/${newEmployee.id}`);
      }
    } catch {
      toast({
        title: 'Error',
        description: isEditing ? 'Failed to update employee' : 'Failed to create employee',
        variant: 'destructive',
      });
    }
  };

  if (isLoadingEmployee) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const isSubmitting = createEmployee.isPending || updateEmployee.isPending;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {isEditing ? 'Edit Employee' : 'Add Employee'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEditing ? 'Update employee details' : 'Create a new employee record'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Tabs defaultValue="personal" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 lg:w-auto">
            <TabsTrigger value="personal">Personal</TabsTrigger>
            <TabsTrigger value="employment">Employment</TabsTrigger>
            <TabsTrigger value="pay">Pay</TabsTrigger>
            <TabsTrigger value="tax">Tax & Super</TabsTrigger>
            <TabsTrigger value="bank">Bank</TabsTrigger>
          </TabsList>

          {/* Personal Details Tab */}
          <TabsContent value="personal" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Personal Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="code">Employee Code *</Label>
                    <Input
                      id="code"
                      value={formData.code}
                      onChange={(e) => handleChange('code', e.target.value)}
                      placeholder="EMP001"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input
                      id="firstName"
                      value={formData.firstName}
                      onChange={(e) => handleChange('firstName', e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => handleChange('lastName', e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      placeholder="(02) 9876 5432"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mobile">Mobile</Label>
                    <Input
                      id="mobile"
                      value={formData.mobile}
                      onChange={(e) => handleChange('mobile', e.target.value)}
                      placeholder="0412 345 678"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="dateOfBirth">Date of Birth</Label>
                    <Input
                      id="dateOfBirth"
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="gender">Gender</Label>
                    <Select
                      value={formData.gender}
                      onValueChange={(value) => handleChange('gender', value)}
                    >
                      <SelectTrigger id="gender">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                        <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Address</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="streetAddress1">Street Address</Label>
                  <Input
                    id="streetAddress1"
                    value={formData.streetAddress1}
                    onChange={(e) => handleChange('streetAddress1', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="streetAddress2">Street Address 2</Label>
                  <Input
                    id="streetAddress2"
                    value={formData.streetAddress2}
                    onChange={(e) => handleChange('streetAddress2', e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="suburb">Suburb</Label>
                    <Input
                      id="suburb"
                      value={formData.suburb}
                      onChange={(e) => handleChange('suburb', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State</Label>
                    <Select
                      value={formData.state}
                      onValueChange={(value) => handleChange('state', value)}
                    >
                      <SelectTrigger id="state">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NSW">NSW</SelectItem>
                        <SelectItem value="VIC">VIC</SelectItem>
                        <SelectItem value="QLD">QLD</SelectItem>
                        <SelectItem value="WA">WA</SelectItem>
                        <SelectItem value="SA">SA</SelectItem>
                        <SelectItem value="TAS">TAS</SelectItem>
                        <SelectItem value="ACT">ACT</SelectItem>
                        <SelectItem value="NT">NT</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="postcode">Postcode</Label>
                    <Input
                      id="postcode"
                      value={formData.postcode}
                      onChange={(e) => handleChange('postcode', e.target.value)}
                      maxLength={4}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Employment Details Tab */}
          <TabsContent value="employment" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Employment Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="employmentType">Employment Type *</Label>
                    <Select
                      value={formData.employmentType}
                      onValueChange={(value) => handleChange('employmentType', value)}
                    >
                      <SelectTrigger id="employmentType">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {EMPLOYMENT_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="employmentStatus">Status</Label>
                    <Select
                      value={formData.employmentStatus}
                      onValueChange={(value) => handleChange('employmentStatus', value)}
                    >
                      <SelectTrigger id="employmentStatus">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {EMPLOYMENT_STATUSES.map((status) => (
                          <SelectItem key={status.value} value={status.value}>
                            {status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="startDate">Start Date *</Label>
                    <Input
                      id="startDate"
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => handleChange('startDate', e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="endDate">End Date</Label>
                    <Input
                      id="endDate"
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => handleChange('endDate', e.target.value)}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="jobTitle">Job Title *</Label>
                    <Input
                      id="jobTitle"
                      value={formData.jobTitle}
                      onChange={(e) => handleChange('jobTitle', e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="department">Department</Label>
                    <Input
                      id="department"
                      value={formData.department}
                      onChange={(e) => handleChange('department', e.target.value)}
                      placeholder="e.g. Sales, Operations"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pay Details Tab */}
          <TabsContent value="pay" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Pay Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="payType">Pay Type *</Label>
                    <Select
                      value={formData.payType}
                      onValueChange={(value) => handleChange('payType', value)}
                    >
                      <SelectTrigger id="payType">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAY_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="payRate">Pay Rate (AUD$) *</Label>
                    <Input
                      id="payRate"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.payRate}
                      onChange={(e) => handleChange('payRate', parseFloat(e.target.value) || 0)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="payFrequency">Pay Frequency *</Label>
                    <Select
                      value={formData.payFrequency}
                      onValueChange={(value) => handleChange('payFrequency', value)}
                    >
                      <SelectTrigger id="payFrequency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAY_FREQUENCIES.map((freq) => (
                          <SelectItem key={freq.value} value={freq.value}>
                            {freq.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tax & Super Tab */}
          <TabsContent value="tax" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Tax Declaration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="tfn">Tax File Number (TFN)</Label>
                    <Input
                      id="tfn"
                      value={formData.tfn}
                      onChange={(e) => handleChange('tfn', e.target.value)}
                      placeholder="XXX XXX XXX"
                      maxLength={11}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="taxScale">Tax Scale</Label>
                    <Select
                      value={formData.taxScale}
                      onValueChange={(value) => handleChange('taxScale', value)}
                    >
                      <SelectTrigger id="taxScale">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TAX_SCALES.map((scale) => (
                          <SelectItem key={scale.value} value={scale.value}>
                            {scale.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.claimTaxFreeThreshold}
                      onChange={(e) => handleChange('claimTaxFreeThreshold', e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    Claim tax-free threshold
                  </Label>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.hasHelpDebt}
                      onChange={(e) => handleChange('hasHelpDebt', e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    Has HELP/SSL/TSL debt
                  </Label>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.hasFinancialSupplementDebt}
                      onChange={(e) => handleChange('hasFinancialSupplementDebt', e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    Has Financial Supplement debt
                  </Label>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Superannuation</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="superFundId">Super Fund</Label>
                    <Select
                      value={formData.superFundId}
                      onValueChange={(value) => handleChange('superFundId', value)}
                    >
                      <SelectTrigger id="superFundId">
                        <SelectValue placeholder="Select super fund" />
                      </SelectTrigger>
                      <SelectContent>
                        {superFunds.map((fund) => (
                          <SelectItem key={fund.id} value={fund.id}>
                            {fund.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="superMemberNumber">Member Number</Label>
                    <Input
                      id="superMemberNumber"
                      value={formData.superMemberNumber}
                      onChange={(e) => handleChange('superMemberNumber', e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="superContributionRate">Super Contribution Rate (%)</Label>
                  <Input
                    id="superContributionRate"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.superContributionRate}
                    onChange={(e) => handleChange('superContributionRate', parseFloat(e.target.value) || 0)}
                  />
                  <p className="text-sm text-muted-foreground">
                    Current Super Guarantee rate is 11.5%
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bank Details Tab */}
          <TabsContent value="bank" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Bank Account Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bankBsb">BSB</Label>
                    <Input
                      id="bankBsb"
                      value={formData.bankBsb}
                      onChange={(e) => handleChange('bankBsb', e.target.value)}
                      placeholder="000-000"
                      maxLength={7}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bankAccountNumber">Account Number</Label>
                    <Input
                      id="bankAccountNumber"
                      value={formData.bankAccountNumber}
                      onChange={(e) => handleChange('bankAccountNumber', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bankAccountName">Account Name</Label>
                    <Input
                      id="bankAccountName"
                      value={formData.bankAccountName}
                      onChange={(e) => handleChange('bankAccountName', e.target.value)}
                      placeholder="Full name on account"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-4 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Save className="mr-2 h-4 w-4" />
            {isEditing ? 'Update Employee' : 'Create Employee'}
          </Button>
        </div>
      </form>
    </div>
  );
}
